import type { InternalRefetchQueryDescriptor } from '@apollo/client';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
  GetEventsDocument,
  GetMessagesDocument,
  GetPatientMessagesDocument,
  GetWebVisitorMessagesDocument,
} from '~/graphql/generated/graphql';
import { messageModalState } from '~/state/partials/message_modal/atoms';
import { profileDrawerState } from '~/state/partials/patient_profile_drawer/atoms';
import { webVisitorProfileDrawerState } from '~/state/reception/atoms';
import { formatISOString } from '~/utils/date';

export const useBuildRefetchQueries = () => {
  const patientDrawerState = useAtomValue(profileDrawerState);
  const modalState = useAtomValue(messageModalState);
  const webVisitorDrawerState = useAtomValue(webVisitorProfileDrawerState);

  const refetchQueries: InternalRefetchQueryDescriptor[] = useMemo(() => {
    const queries: InternalRefetchQueryDescriptor[] = [
      {
        query: GetMessagesDocument,
        variables: {
          page: 1,
          perPage: 25,
          date: formatISOString(new Date()),
        },
      },
    ];

    if (!modalState.profile) return queries;

    const guestPatientId =
      modalState.profile?.type !== 'Patient' ? modalState.patientId : undefined;
    const patientId = modalState.profile?.type === 'Patient' ? modalState.patientId : undefined;

    queries.push({
      query: GetEventsDocument,
      variables: {
        patientId,
        guestPatientId,
        page: modalState.page,
        perPage: modalState.perPage,
      },
    });

    if (webVisitorDrawerState.isOpen && webVisitorDrawerState.webVisitorId) {
      queries.push({
        query: GetWebVisitorMessagesDocument,
        variables: { id: webVisitorDrawerState.webVisitorId },
      });
    }

    if (patientDrawerState.isOpen && patientDrawerState.patientId) {
      queries.push({
        query: GetPatientMessagesDocument,
        variables: { id: patientDrawerState.patientId },
      });
    }

    return queries;
  }, [
    modalState.page,
    modalState.patientId,
    modalState.perPage,
    modalState.profile,
    patientDrawerState.isOpen,
    patientDrawerState.patientId,
    webVisitorDrawerState.isOpen,
    webVisitorDrawerState.webVisitorId,
  ]);

  return refetchQueries;
};
